import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { initials } from "@/lib/format";
import { toast } from "sonner";
import { Users, UserPlus, Loader2, History, CheckCircle2, HelpCircle, Repeat, XCircle } from "lucide-react";

const LABEL = {
  pending_acknowledgement: "Pending Acknowledgement", acknowledged: "Acknowledged",
  clarification_requested: "Clarification Requested", reassignment_requested: "Reassignment Requested",
  ended: "Ended",
};
const TONE = {
  pending_acknowledgement: "bg-amber-100 text-amber-800 border-amber-200",
  acknowledged: "bg-emerald-100 text-emerald-800 border-emerald-200",
  clarification_requested: "bg-sky-100 text-sky-800 border-sky-200",
  reassignment_requested: "bg-orange-100 text-orange-800 border-orange-200",
  ended: "bg-muted text-muted-foreground",
};
const EMPTY_BRIEF = { recruiter_id: "", responsibility: "Supporting", is_primary: false, target: "", deadline: "", screening_criteria: "", instructions: "" };

export default function RecruiterAssignments({ job, onChanged }) {
  const { can, user } = useAuth();
  const [people, setPeople] = useState({ recruiters: [], bdms: [] });
  const [sheet, setSheet] = useState(null); // {mode:'assign'|'edit'|'reassign'|'end', assignment}
  const [form, setForm] = useState(EMPTY_BRIEF);
  const [reason, setReason] = useState("");
  const [toRecruiter, setToRecruiter] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyOf, setHistoryOf] = useState(null);
  const [actionMode, setActionMode] = useState(null);
  const [actionNote, setActionNote] = useState("");

  const manage = can("job:edit");
  const assignments = (job.assigned_recruiters || []).filter((a) => typeof a === "object");
  const mine = assignments.find((a) => a.recruiter_id === user?.id && a.status !== "ended");

  const loadPeople = useCallback(async () => {
    if (!manage) return;
    try {
      const { data } = await api.get(`/jobs/${job.id}/assignment-options`);
      setPeople({
        recruiters: data.recruiters || [],
        bdms: data.bdms || [],
      });
    } catch {
      setPeople({ recruiters: [], bdms: [] });
    }
  }, [job.id, manage]);
  useEffect(() => { loadPeople(); }, [loadPeople]);

  const openSheet = (mode, assignment) => {
    setSheet({ mode, assignment });
    setReason(""); setToRecruiter("");
    setForm(assignment ? { ...EMPTY_BRIEF, ...assignment, target: assignment.target ?? "", deadline: (assignment.deadline || "").slice(0, 10) } : EMPTY_BRIEF);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (sheet.mode === "assign") {
        if (!form.recruiter_id) { toast.error("Select a recruiter"); setBusy(false); return; }
        await api.post(`/jobs/${job.id}/recruiters`, {
          recruiter_id: form.recruiter_id, responsibility: form.responsibility, is_primary: form.is_primary,
          target: form.target === "" ? null : Number(form.target), deadline: form.deadline || null,
          screening_criteria: form.screening_criteria || null, instructions: form.instructions || null });
        toast.success("Recruiter assigned — acknowledgement pending");
      } else if (sheet.mode === "edit") {
        await api.put(`/jobs/${job.id}/recruiters/${sheet.assignment.recruiter_id}`, {
          responsibility: form.responsibility, target: form.target === "" ? null : Number(form.target),
          deadline: form.deadline || null, screening_criteria: form.screening_criteria || null,
          instructions: form.instructions || null });
        toast.success("Assignment brief updated");
      } else if (sheet.mode === "reassign") {
        if (!toRecruiter) { toast.error("Choose the new recruiter"); setBusy(false); return; }
        if (!reason.trim()) { toast.error("A reason is required"); setBusy(false); return; }
        await api.post(`/jobs/${job.id}/recruiters/${sheet.assignment.recruiter_id}/reassign`, { to_recruiter_id: toRecruiter, reason });
        toast.success("Assignment reassigned");
      } else {
        if (!reason.trim()) { toast.error("A reason is required"); setBusy(false); return; }
        await api.post(`/jobs/${job.id}/recruiters/${sheet.assignment.recruiter_id}/end`, { reason });
        toast.success("Assignment ended (kept in history)");
      }
      setSheet(null); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const act = async (action) => {
    if (action !== "acknowledge" && !actionNote.trim()) { toast.error("Please describe your request"); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/recruiters/me/action`, { action, note: actionNote || null });
      toast.success(action === "acknowledge" ? "Assignment acknowledged" : "Request sent");
      setActionMode(null); setActionNote(""); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="recruiter-assignments">
      {mine && (
        <Card className="p-5 border-[#255058]/30 bg-[#255058]/5" data-testid="my-assignment-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-display font-semibold">Your assignment</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline" className={`text-[10px] ${TONE[mine.status]}`}>{LABEL[mine.status]}</Badge>
                {mine.target != null && <span>Target: {mine.target} submissions</span>}
                {mine.deadline && <span>Deadline: {fmtDate(mine.deadline)}</span>}
              </div>
            </div>
            {actionMode ? (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <Textarea rows={2} className="sm:w-72" data-testid="assignment-action-note" placeholder="Reason / question" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setActionMode(null); setActionNote(""); }}>Cancel</Button>
                  <Button size="sm" className="bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={busy} onClick={() => act(actionMode)} data-testid="assignment-action-submit">Send</Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {mine.status === "pending_acknowledgement" && (
                  <Button size="sm" className="gap-1.5 bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={busy} onClick={() => act("acknowledge")} data-testid="assignment-acknowledge">
                    <CheckCircle2 className="h-4 w-4" /> Acknowledge
                  </Button>
                )}
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setActionMode("clarification")} data-testid="assignment-clarify">
                  <HelpCircle className="h-4 w-4" /> Ask clarification
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setActionMode("reassignment")} data-testid="assignment-request-reassign">
                  <Repeat className="h-4 w-4" /> Request reassignment
                </Button>
              </div>
            )}
          </div>
          {(mine.screening_criteria || mine.instructions) && (
            <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
              {mine.screening_criteria && <div><div className="text-xs font-semibold text-muted-foreground">Screening criteria</div><p className="whitespace-pre-wrap">{mine.screening_criteria}</p></div>}
              {mine.instructions && <div><div className="text-xs font-semibold text-muted-foreground">Instructions</div><p className="whitespace-pre-wrap">{mine.instructions}</p></div>}
            </div>
          )}
        </Card>
      )}

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-semibold"><Users className="h-4 w-4 text-[#255058]" /> Recruiter Assignments</div>
          {manage && <Button size="sm" className="gap-1.5 bg-[#255058] text-white hover:bg-[#1c3d43]" onClick={() => openSheet("assign")} data-testid="assign-recruiter-button"><UserPlus className="h-4 w-4" /> Assign recruiter</Button>}
        </div>
        <div className="space-y-2">
          {(job.bdm_name || people.bdms.length > 0) && (
            <div
              className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border pb-3 text-xs"
              data-testid="job-bdm-directory"
            >
              <span className="font-semibold text-muted-foreground">BDM team:</span>
              {people.bdms.length > 0
                ? people.bdms.map((bdm) => (
                    <span key={bdm.id} className="font-medium">
                      {bdm.name}
                      {bdm.id === job.bdm_id ? " (assigned)" : ""}
                    </span>
                  ))
                : <span className="font-medium">{job.bdm_name || "Unassigned"}</span>}
            </div>
          )}
          {assignments.map((a) => (
            <div key={a.assignment_id || a.recruiter_id} className={`rounded-lg border border-border p-3 ${a.status === "ended" ? "opacity-70" : ""}`} data-testid={`assignment-row-${a.recruiter_id || a.assignment_id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-[#255058] text-xs text-white">{initials(a.recruiter_name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{a.recruiter_name}</span>
                    <Badge variant="outline" className={`text-[10px] ${TONE[a.status] || ""}`}>{LABEL[a.status] || a.status}</Badge>
                    {a.recruiter_id === job.primary_recruiter_id && <Badge className="status-purple border-0 text-[10px]">Primary</Badge>}
                    {a.legacy_migrated && <Badge variant="outline" className="text-[10px]">Migrated</Badge>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>{a.responsibility}</span>
                    <span>Assigned {fmtDate(a.assigned_at)} by {a.assigned_by}</span>
                    {a.target != null && <span>Target {a.target}</span>}
                    {a.deadline && <span>Deadline {fmtDate(a.deadline)}</span>}
                  </div>
                  {a.end_reason && <div className="mt-1 text-xs text-[#c65a63]">Ended: {a.end_reason}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setHistoryOf(a)} data-testid={`assignment-history-${a.recruiter_id || a.assignment_id}`}><History className="h-3.5 w-3.5" /> History</Button>
                  {manage && a.status !== "ended" && <>
                    <Button size="sm" variant="outline" onClick={() => openSheet("edit", a)} data-testid={`assignment-edit-${a.recruiter_id}`}>Brief</Button>
                    <Button size="sm" variant="outline" onClick={() => openSheet("reassign", a)} data-testid={`assignment-reassign-${a.recruiter_id}`}>Reassign</Button>
                    <Button size="sm" variant="outline" className="gap-1.5 text-[#c65a63]" onClick={() => openSheet("end", a)} data-testid={`assignment-end-${a.recruiter_id}`}><XCircle className="h-3.5 w-3.5" /> End</Button>
                  </>}
                </div>
              </div>
            </div>
          ))}
          {!assignments.length && <div className="text-sm text-muted-foreground">No recruiters assigned yet.</div>}
        </div>
      </Card>

      <Sheet open={!!sheet} onOpenChange={(o) => !o && setSheet(null)}>
        <SheetContent className="w-full overflow-y-auto scrollbar-thin sm:max-w-lg" data-testid="assignment-sheet">
          <SheetHeader>
            <SheetTitle className="font-display">
              {sheet?.mode === "assign" ? "Assign recruiter" : sheet?.mode === "edit" ? "Assignment brief" : sheet?.mode === "reassign" ? "Reassign" : "End assignment"}
            </SheetTitle>
            <SheetDescription>
              {sheet?.mode === "end" ? "The assignment stays visible as Ended — nothing is deleted." : "The recruiter receives a task and a notification."}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4 pb-8">
            {sheet?.mode === "assign" && (
              <F label="Recruiter *">
                <Select value={form.recruiter_id} onValueChange={(v) => setForm({ ...form, recruiter_id: v })}>
                  <SelectTrigger data-testid="assign-recruiter-select"><SelectValue placeholder="Select recruiter" /></SelectTrigger>
                  <SelectContent>{people.recruiters.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — Recruiter</SelectItem>)}</SelectContent>
                </Select>
              </F>
            )}
            {(sheet?.mode === "assign" || sheet?.mode === "edit") && (<>
              <F label="Responsibility">
                <Select value={form.responsibility} onValueChange={(v) => setForm({ ...form, responsibility: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Primary", "Supporting", "Sourcing", "Screening"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <div className="grid grid-cols-2 gap-4">
                <F label="Target (submissions)"><Input type="number" data-testid="assign-target" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></F>
                <F label="Deadline"><Input type="date" data-testid="assign-deadline" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></F>
              </div>
              <F label="Screening criteria"><Textarea rows={3} data-testid="assign-criteria" value={form.screening_criteria || ""} onChange={(e) => setForm({ ...form, screening_criteria: e.target.value })} /></F>
              <F label="Instructions"><Textarea rows={3} data-testid="assign-instructions" value={form.instructions || ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></F>
              {sheet?.mode === "assign" && (
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="assign-primary" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} /> Primary recruiter</label>
              )}
            </>)}
            {sheet?.mode === "reassign" && (
              <F label="New recruiter *">
                <Select value={toRecruiter} onValueChange={setToRecruiter}>
                  <SelectTrigger data-testid="reassign-select"><SelectValue placeholder="Select recruiter" /></SelectTrigger>
                  <SelectContent>{people.recruiters.filter((p) => p.id !== sheet.assignment.recruiter_id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — Recruiter</SelectItem>)}</SelectContent>
                </Select>
              </F>
            )}
            {(sheet?.mode === "reassign" || sheet?.mode === "end") && (
              <F label="Reason *"><Textarea rows={3} data-testid="assignment-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></F>
            )}
            <Button className="w-full bg-[#255058] text-white hover:bg-[#1c3d43]" disabled={busy} onClick={save} data-testid="assignment-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!historyOf} onOpenChange={(o) => !o && setHistoryOf(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="assignment-history-sheet">
          <SheetHeader><SheetTitle className="font-display">{historyOf?.recruiter_name}</SheetTitle>
            <SheetDescription>Append-only assignment history.</SheetDescription></SheetHeader>
          <div className="mt-6 space-y-3">
            {(historyOf?.history || []).map((h, i) => (
              <div key={i} className="rounded-lg border border-border p-3 text-sm">
                <div className="font-medium">{LABEL[h.status] || h.status}</div>
                <div className="text-xs text-muted-foreground">{h.by} · {fmtDate(h.at)}</div>
                {h.note && <div className="mt-1 text-xs">{h.note}</div>}
              </div>
            ))}
            {!(historyOf?.history || []).length && <div className="text-sm text-muted-foreground">No history recorded.</div>}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }
